import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Menu, ShoppingBag, Package, DollarSign, Users, TrendingUp, ArrowUpRight, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import AdminSidebar from '../../components/AdminSidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_orders: 0,
    pending_orders: 0,
    delivered_orders: 0,
    cancelled_orders: 0,
    total_revenue: 0,
    cod_revenue: 0,
    online_revenue: 0,
    total_visitors: 0,
    total_cart_adds: 0,
    total_checkouts: 0,
    conversion_rate: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);

  useEffect(() => {
    const checkAuthAndFetch = () => {
      const isLoggedIn = localStorage.getItem('admin_logged_in');
      if (isLoggedIn !== 'true') {
        navigate('/admin', { replace: true });
        return;
      }
      fetchData();
    };
    
    checkAuthAndFetch();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/dashboard`),
        axios.get(`${API_URL}/api/orders`)
      ]);
      
      setStats(statsRes.data);
      setRecentOrders(ordersRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const statCards = [
    {
      title: 'Total Revenue',
      value: formatCurrency(stats.total_revenue),
      icon: DollarSign,
      color: 'bg-gold/10 text-gold',
      trend: '+12%'
    },
    {
      title: 'Total Orders',
      value: stats.total_orders,
      icon: ShoppingBag,
      color: 'bg-maroon/10 text-maroon',
      trend: '+8%'
    },
    {
      title: 'Pending Orders',
      value: stats.pending_orders,
      icon: Clock,
      color: 'bg-saffron/10 text-saffron',
      trend: null
    },
    {
      title: 'Visitors',
      value: stats.total_visitors,
      icon: Users,
      color: 'bg-blue-100 text-blue-600',
      trend: '+24%'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-maroon"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <AdminSidebar active="dashboard" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:ml-64">
        <header className="bg-white border-b border-gold/10 px-4 py-4 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden"
            >
              <Menu className="w-6 h-6" />
            </Button>
            <div>
              <h1 className="text-xl font-heading font-bold text-maroon">Dashboard</h1>
              <p className="text-sm text-maroon/60">Welcome back, Admin</p>
            </div>
          </div>
          <a 
            href="/" 
            target="_blank" 
            className="text-sm text-maroon hover:underline"
          >
            View Site →
          </a>
        </header>

        <main className="p-4 md:p-6">
          {/* Stat Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {statCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-5 border border-gold/10 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    {stat.trend && (
                      <span className="flex items-center text-sm font-medium text-green-600">
                        <ArrowUpRight className="w-4 h-4" />
                        {stat.trend}
                      </span>
                    )}
                  </div>
                  <p className="text-2xl font-bold text-maroon mb-1">{stat.value}</p>
                  <p className="text-sm text-maroon/60">{stat.title}</p>
                </div>
              );
            })}
          </div>

          {/* Revenue Breakdown & Recent Orders */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Breakdown */}
            <div className="bg-white rounded-2xl p-6 border border-gold/10">
              <h2 className="text-lg font-heading font-semibold text-maroon mb-6">Revenue Breakdown</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-cream rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-maroon/10 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-maroon" />
                    </div>
                    <div>
                      <p className="font-medium text-maroon">Online Payments</p>
                      <p className="text-sm text-maroon/60">Cards, UPI, Net Banking</p>
                    </div>
                  </div>
                  <p className="font-bold text-maroon">{formatCurrency(stats.online_revenue)}</p>
                </div>

                <div className="flex items-center justify-between p-4 bg-cream rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center">
                      <Package className="w-5 h-5 text-gold" />
                    </div>
                    <div>
                      <p className="font-medium text-maroon">Cash on Delivery</p>
                      <p className="text-sm text-maroon/60">Pay on delivery orders</p>
                    </div>
                  </div>
                  <p className="font-bold text-maroon">{formatCurrency(stats.cod_revenue)}</p>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gold/10">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-maroon/60">Conversion Rate</p>
                  <p className="text-xl font-bold text-maroon">{stats.conversion_rate}%</p>
                </div>
              </div>
            </div>

            {/* Recent Orders */}
            <div className="bg-white rounded-2xl p-6 border border-gold/10">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-heading font-semibold text-maroon">Recent Orders</h2>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/admin/orders')}
                  className="text-maroon hover:text-maroon-dark"
                >
                  View All
                </Button>
              </div>

              {recentOrders.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingBag className="w-12 h-12 text-maroon/20 mx-auto mb-3" />
                  <p className="text-maroon/60">No orders yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentOrders.map((order) => (
                    <div
                      key={order.id}
                      className="flex items-center justify-between p-3 bg-cream rounded-xl hover:bg-cream-dark transition-colors cursor-pointer"
                      onClick={() => navigate('/admin/orders')}
                    >
                      <div>
                        <p className="font-medium text-maroon">{order.customer_name}</p>
                        <p className="text-sm text-maroon/60">{order.id}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-maroon">{formatCurrency(order.total_amount)}</p>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          order.order_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          order.order_status === 'delivered' ? 'bg-green-100 text-green-700' :
                          order.order_status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {order.order_status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-6 bg-white rounded-2xl p-6 border border-gold/10">
            <h2 className="text-lg font-heading font-semibold text-maroon mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/admin/products')}
                className="h-auto py-4 flex flex-col items-center gap-2 border-maroon/20 hover:bg-cream"
              >
                <Package className="w-6 h-6 text-maroon" />
                <span>Add Product</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/admin/orders')}
                className="h-auto py-4 flex flex-col items-center gap-2 border-maroon/20 hover:bg-cream"
              >
                <ShoppingBag className="w-6 h-6 text-maroon" />
                <span>View Orders</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/admin/homepage')}
                className="h-auto py-4 flex flex-col items-center gap-2 border-maroon/20 hover:bg-cream"
              >
                <TrendingUp className="w-6 h-6 text-maroon" />
                <span>Edit Homepage</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/admin/settings')}
                className="h-auto py-4 flex flex-col items-center gap-2 border-maroon/20 hover:bg-cream"
              >
                <Users className="w-6 h-6 text-maroon" />
                <span>Site Settings</span>
              </Button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
